<template>
  <div class="paymentDue" v-if="displayPaymentPastDue">
    <div class="paymentDue_title">
      <h3>
        {{ $t('myaccount.paymentDetails.paymentDue.title', 'Your payment is past due.') }}
      </h3>
      <p>
        {{ $t('myaccount.paymentDetails.paymentDue.subTitle', 'Please pay immediately to avoid delivery delays or your subscription(s) cancellation.') }}
      </p>
    </div>
    <ul class="invoice_list">
      <li
        class="billingItem"
        v-for="(item, index) in billings"
        v-if="item.pdfFileId"
        :key="index"
      >
        <div class="flex-between items">
          <span class="date">
            {{ item.invoiceDate | formatLocalDate }}
          </span>
          <div class="priceWithButton">
            <span class="price">
              -{{ item.balance | formatCurrency }}
            </span>
            <a
              class="AppButton button-orange hide-mobile"
              :class="{'grey-color': paymentActivity.hasPaymentInProgress}"
              @mouseover="mouseover(item.pdfFileId)"
              @mouseleave="mouseleave(item.pdfFileId)"
              @click.prevent.stop="payNowTask.run(item.invoiceId)"
            >
              {{ $t('myaccount.paymentDetails.paymentDue.button', 'Pay now') }}
            </a>
            <v-popover
              offset="15"
              trigger="manual"
              placement="bottom-right"
              :open="isOpen && !!showInvoice[item.pdfFileId]"
              popover-wrapper-class="pastdue_poptip"
            >
              <template slot="popover">
                <div>{{ $t('common.paymentInprogress.toolTips.text', 'This option is disabled as we are processing your latest payment') }}</div>
              </template>
            </v-popover>
          </div>
          <a
            v-if="showInvoiceLink"
            class="downloadLink"
            :href="item.pdfFileId"
            @click.prevent.stop="downloadInvoiceTask.run(item.pdfFileId, item.pdfFileUrl)"
          >
            {{ $t('myaccount.billing.load.link', 'Invoice') }}
            ›
          </a>
        </div>
        <a
          class="AppButton button-orange hide-desktop hide-tablet"
          :class="{'grey-color': paymentActivity.hasPaymentInProgress}"
          @click.prevent.stop="payNowTask.run(item.invoiceId)"
          @mouseover="mouseover(item.pdfFileId)"
          @mouseleave="mouseleave(item.pdfFileId)"
        >
          {{ $t('myaccount.paymentDetails.paymentDue.button', 'Pay now') }}
        </a>
        <v-popover
          offset="15"
          trigger="manual"
          placement="bottom-right"
          :open="isOpen && !!showInvoice[item.pdfFileId]"
          popover-wrapper-class="pastdue_poptip_mobile"
        >
          <template slot="popover">
            <div>{{ $t('common.paymentInprogress.toolTips.text', 'This option is disabled as we are processing your latest payment') }}</div>
          </template>
        </v-popover>
      </li>
    </ul>
  </div>
</template>

<script>
  import { extractErrorMessage } from '@/utilities/common'
  import { detectedCulture } from '@/utilities/common/culture'
  import Vue from 'vue'
  import { mapGetters } from 'vuex'

  export default {
    name: 'payment-due',

    props: {
      billings: {
        type: Array,
        required: true
      }
    },

    data () {
      return {
        isOpen: false,
        showInvoice: []
      }
    },

    computed: {
      showInvoiceLink () {
        return detectedCulture.countryCode !== 'DE' && detectedCulture.countryCode !== 'IT' && detectedCulture.countryCode !== 'GB' && detectedCulture.countryCode !== 'AT'
      },
      ...mapGetters('account', ['paymentActivity']),

      displayPaymentPastDue () {
        return this.billings.length > 0
      }
    },

    watch: {
      paymentActivity (newVal, oldVal) {
        try {
          if (oldVal.hasPaymentInProgress && !newVal.paymentActivity) {
            this.$store.dispatch('account/fetchBillingInvoices')
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            title: extractErrorMessage(error)
          })
        }
      }
    },

    methods: {
      mouseleave (pdfFileId) {
        if (this.paymentActivity.hasPaymentInProgress) {
          this.isOpen = false
          this.showInvoice[pdfFileId] = false
        }
      },
      mouseover (pdfFileId) {
        if (this.paymentActivity.hasPaymentInProgress) {
          this.isOpen = true
          this.showInvoice[pdfFileId] = true
        }
      }
    },

    tasks (t) {
      return {
        downloadInvoiceTask: t(async function (fileId, fileUrl) {
          this.trackConversion({ name: 'download', url: fileUrl })
          document.querySelector(`[href='${fileId}']`).classList.add('downloading')
          await Vue.api.billing.getInvoiceFileById(fileId)
            .then((data) => {
              let downloadLink = document.createElement('a')
              downloadLink.style.display = 'none'
              downloadLink.href = data.link
              document.body.appendChild(downloadLink)
              downloadLink.click()

              setTimeout(() => {
                document.body.removeChild(downloadLink)
                document.querySelector(`[href='${fileId}']`).classList.remove('downloading')
                document.querySelector(`[href='${fileId}']`).classList.add('downloaded')
              }, 500)
            }).onError(function (task) {
              let errorTitle
              if (task.error.status === 401) {
                errorTitle = extractErrorMessage(task.error)
              } else {
                errorTitle = this.$t('validation.error.message.invoiceFileIsNotFound', 'The invoice file is not found.')
              }
              this.$notify({
                type: 'error',
                title: errorTitle
              })
            }).flow('restart')
        }),

        payNowTask: t(async function (fileId) {
          this.trackConversion({ name: 'interation', description: 'button:pay_now' })
          if (this.paymentActivity.hasPaymentInProgress) {
            return false
          } else {
            this.$router.replace({
              name: 'subscription-payment-detail',
              params: {
                id: fileId
              }
            })
          }
        })
      }
    }
  }
</script>

<style lang="scss">
  .paymentDue {
    border: .01rem solid #de7815;
    background-color: #fdf5d7;
    margin-bottom: .3rem;

    @include media(tablet) {
      margin-top: -.22rem;
    }

    &_title {
      padding: .15rem .16rem .25rem;
      color: #de7815;

      h3 {
        font-size: .21rem;
        line-height: .22rem;
        margin-bottom: .09rem;
      }

      p {
        font-size: .16rem;
        line-height: .22rem;
      }
    }

    .invoice_list {
      padding: 0 .04rem .06rem;

      .billingItem {
        padding: .08rem .1rem;
        background: #fff;
        line-height: .22rem;
      }

      .date {
        font-size: .19rem;
        color: #4a4a4a;
        font-weight: 500;
        flex-shrink: 0;
        min-width: 1.1rem;
      }

      .priceWithButton {
        display: flex;
        justify-content: space-between;

        @include media(desktop) {
          min-width: 2rem;
        }

        .price {
          font-size: .17rem;
          font-weight: 400;
          line-height: .35rem;
          color: $font-black;
        }
      }

      .downloadLink {
        cursor: pointer;
        color: $blue;
        font-size: .14rem;
        font-weight: 500;
        line-height: 1.5;

        &.downloading {
          color: #d8d8d8;
          pointer-events: none;
        }

        &.downloaded {
          color: $pink;
        }
      }
    }

    .grey-color {
      background: #d8d8d8 !important;
      cursor: default;
    }
  }

  .pastdue_poptip {
    top: -.36rem;
    left: .2rem;
    position: absolute;
    background: white;
    min-width: 2.09rem;
    display: none;

    @include media(tablet) {
      top: 0;
      left: -.89rem;
      position: absolute;
      background: white;
      min-width: 2.26rem;
      display: block;
    }

    .tooltip-arrow {
      @include media(tablet) {
        left: calc(20% - 9px) !important;
      }

      @include media(desktop) {
        left: calc(20% - 9px) !important;
      }
    }

    @include media(desktop) {
      display: block;
    }
  }

  .pastdue_poptip_mobile {
    top: -.36rem;
    left: 1rem;
    position: absolute;
    background: white;
    min-width: 2.09rem;
    display: block;

    @include media(tablet) {
      display: none;
    }

    @include media(desktop) {
      display: none;
    }

    .tooltip-arrow {
      @include media(tablet) {
        left: calc(20% - 9px) !important;
      }

      @include media(desktop) {
        left: calc(20% - 9px) !important;
      }
    }
  }
</style>
